import React, { useCallback, useEffect, useRef, useState } from "react";

import Textarea from "../../../components/form-item/Textarea";
import Toggle from "../../../components/form-item/Toggle";
import TitleBlock from "../../../components/block/TitleBlock";
import StatusIndicator, { StatusType } from "../../../components/status-indicator/StatusIndicator";
import StatementPreview from "../../profile/components/ProfileStatement";
import {
  useGetProfileContentQuery,
  useUpdateProfileContentMutation,
} from "../../../app/services/creatorProfile";
import { useGetCurrentUserQuery } from "../../../app/accounts/authuser";
import type { ProfileStatement } from "../../../types/profile";

const EMPTY: ProfileStatement = {
  theme_jp: "",
  theme_en: "",
  is_show_avatar: false,
  is_show_name: false,
  is_show_title: false,
};

const DEBOUNCE_MS = 600;

/**
 * アーティストステートメント編集（マイページ同様のリアルタイム保存）。
 *
 * テキストはデバウンス保存、トグルは即時保存する（保存ボタンなし）。本文の
 * 改行はそのまま保存し、公開ページで「段落全体を枠幅に最適化」表示する。
 * 公開ページと同じ表示（ProfileStatement）をフォーム上にプレビュー表示する。
 */
const MySiteState: React.FC = () => {
  const { data: contentResp } = useGetProfileContentQuery();
  const { data: currentUser } = useGetCurrentUserQuery();
  const [updateContent] = useUpdateProfileContentMutation();
  const [statement, setStatement] = useState<ProfileStatement>(EMPTY);
  const [status, setStatus] = useState<StatusType>("idle");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 初回ロードのみ反映し、編集中の自分の入力をサーバ応答で上書きしないようにする。
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!hydratedRef.current && contentResp?.data) {
      setStatement(contentResp.data.statement || EMPTY);
      hydratedRef.current = true;
    }
  }, [contentResp]);

  const save = useCallback(
    async (next: ProfileStatement) => {
      try {
        setStatus("saving");
        await updateContent({ statement: next }).unwrap();
        setStatus("saved");
        setTimeout(() => setStatus("idle"), 1500);
      } catch {
        setStatus("error");
        setTimeout(() => setStatus("idle"), 2000);
      }
    },
    [updateContent],
  );

  const updateText = (patch: Partial<ProfileStatement>) => {
    const next = { ...statement, ...patch };
    setStatement(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => save(next), DEBOUNCE_MS);
  };

  const updateToggle = (patch: Partial<ProfileStatement>) => {
    const next = { ...statement, ...patch };
    setStatement(next);
    save(next);
  };

  // プレビュー用の表示名 / タイトル / アバター（/api/me から）。
  const user = currentUser?.data;
  const displayName = user?.display_name_jp || user?.display_name_en || "";
  const title = user?.title_jp || user?.title_en || "";
  const avatarUrl = user?.avatar_optimized || user?.avatar_original || null;

  return (
    <div className="u-col u-gap16">
      <TitleBlock title="アーティストステートメント" description="入力内容は自動保存されます。改行位置がそのまま公開ページに反映されます。" />

      {/* 実際の表示状態（公開ページと同じ ProfileStatement でプレビュー） */}
      <div className="u-col u-w100 u-gap4">
        <span className="bodytext-11 u-text-light-gray">プレビュー</span>
        <div className="c-group u-w100">
          {statement.theme_jp.trim() || statement.theme_en.trim() ? (
            <StatementPreview
              statement={statement}
              displayName={displayName}
              title={title}
              avatarUrl={avatarUrl}
              language="JP"
            />
          ) : (
            <span className="bodytext-13 u-text-light-gray">テキストを入力するとここに表示されます。</span>
          )}
        </div>
      </div>

      <Textarea
        label="ステートメント（日本語）"
        rows={6}
        maxLength={1000}
        value={statement.theme_jp}
        onChange={(e) => updateText({ theme_jp: e.target.value })}
        placeholder="私たちは、あらゆるシーンに寄り添い、そこでしか味わえない音を創造します。"
      />
      <Textarea
        label="ステートメント（English）"
        rows={6}
        maxLength={1000}
        value={statement.theme_en}
        onChange={(e) => updateText({ theme_en: e.target.value })}
        placeholder="We are close to every scene and create sounds that can only be experienced there."
      />

      <div className="u-col u-w100 u-gap4">
        <div onClick={() => updateToggle({ is_show_avatar: !statement.is_show_avatar })}>
          <Toggle value={statement.is_show_avatar}>アバターを表示</Toggle>
        </div>
        <div onClick={() => updateToggle({ is_show_name: !statement.is_show_name })}>
          <Toggle value={statement.is_show_name}>アーティスト名を表示</Toggle>
        </div>
        <div onClick={() => updateToggle({ is_show_title: !statement.is_show_title })}>
          <Toggle value={statement.is_show_title}>タイトルを表示</Toggle>
        </div>
      </div>

      <div className="u-row-end u-w100">
        <StatusIndicator status={status} inline />
      </div>
    </div>
  );
};

export default MySiteState;
